
declare var window: any;

import { Component, Vue, Prop } from 'vue-property-decorator';
import { etvService } from '../services/etv.service';
import { statisticService, eventType } from '../services/statistic.service';
import { Timer } from '../utils/timer';
import videojs from 'video.js';
import 'videojs-contrib-ads';
import 'videojs-playlist';
import 'videojs-ima';

import registerPrivacyIcon from '../plugins/video-player/PrivacyIcon';
import registerQualitySelector, { EVENTS as QualityEvent } from '@silvermine/videojs-quality-selector';

import { PlayerEvent } from '../interfaces/player-event.enum';

// overlays
import MetaOverlay from '@/components/MetaOverlay.vue';
import LogoOverlay from '@/components/LogoOverlay.vue';
import PlayListControls from '@/components/controls/PlayListControls.vue';

import { logService } from '../services/log.service';
import { adService } from '../services/ad.service';

import { PlayerConfig, EtvPlayer, EtvConfig } from '@/interfaces/player-config.interface';
import { MetaInfo } from '@/interfaces/meta-info.interface';
import { Template } from '../App.vue';
import { iFrameService } from '../services/iframe.service';

import { setSelectedSourcesByQuality } from '@/utils/quality-selector.utils';

// local config while runtime
import localConfig from '../config/local.config.json';

@Component({
  components: {
    MetaOverlay,
    LogoOverlay,
    PlayListControls,
  },
})
export default class Player extends Vue {
  // Types for template refs
  public $refs!: {
    videoEl?: HTMLVideoElement;
  };
  @Prop() public playerConfig!: PlayerConfig;
  @Prop() public etvConfig!: EtvConfig;
  @Prop({ default: 'etv-player' }) public playerId!: string;
  @Prop({ default: false }) public hideOverlays!: boolean;

  // Template data
  public playlist: any[] = [];
  public currentPlaylistIndex: number = 0;
  // Prefetch marker to avoid duplicate prefetching for the same next index
  private prefetchedNextIndex: number | null = null;

  /**
   * Extended video.js Player
   */
  private player!: EtvPlayer;

  /**
   * video.js does not provide better type for options in version 8
   * @see [Video.js Options Reference](https://videojs.com/guides/options/)
   */
  private playerOptions!: any;

  public isLoading: boolean = false;

  public logoVisible: boolean = false; // Shown together with metaVisible in playerOnPlaying()
  public metaVisible: boolean = false; // Shown together with logoVisible in playerOnPlaying()
  public metaInfo: MetaInfo = {};

  private liveTimeout: any;
  private pauseTimer: any;
  private clockTimer: any;
  private savedVolumeBeforeMidroll: number = 1; // Store volume before midroll fade-out

  // status
  private isInitialPlay: boolean = true;
  private isAdComplete: boolean = false;
  private isAdPending: boolean = false;
  // Removed manual ad state management - videojs-ima handles this automatically
  private midrollTimer: any;
  private currentAdType: 'preroll' | 'midroll' | 'postroll' | null = null;
  private midrollCounter: number = 0; // Track number of midrolls played

  // statistic stuff
  private statisticInit: boolean = false;
  private statisticLifecyclePlay: boolean = false;
  private timeUpdateSend = -1;
  private retryOnError = 0;

  public isUserActive: boolean = false;

  private created() {
    const { accountId, autoplay } = this.playerConfig;
    let defaultConfig = localConfig.find((config: any) => config.accountId === 'default') ?? {};
    let accountConfig = localConfig.find((config: any) => config.accountId === accountId) ?? {};

    this.isLoading = autoplay;

    registerPrivacyIcon({ accountConfig, defaultConfig });
    registerQualitySelector(videojs);

    this.playerOptions = {
      // videojs options
      muted: this.playerConfig.mute,
      language: this.playerConfig.language,
      // aspectRatio and fluid removed - we handle sizing manually with CSS padding technique
      autoplay: this.playerConfig.autoplay,
      experimentalSvgIcons: true,
      // playbackRates: [0.7, 1.0, 1.5, 2.0],
      poster: this.playerConfig.previewImage,
      controls: true,
      controlBar: {
        children: ['playToggle', 'progressControl', 'volumePanel', 'privacyIcon', 'qualitySelector', 'fullscreenToggle'],
      },
    };
  }

  private mounted() {
    logService.debug('[PLAYER] Player component mounted - initializing VideoJS');
    this.initializeVideoPlayer();
  }

  private initializeVideoPlayer() {
    this.$nextTick(() => {
      const videoElement = this.$refs.videoEl as HTMLVideoElement | undefined;
      if (videoElement) {
        this.createPlayer(videoElement);
      } else {
        logService.error('[PLAYER] Video element not found via ref');
        this.$emit('templateChange', Template.PLAYER_ERROR);
      }
    });
  }

  /**
   * Create VideoJS player on the given video element
   */
  private createPlayer(videoElement: HTMLVideoElement) {
    logService.debug('[PLAYER] Creating VideoJS player');

    this.player = videojs(videoElement, this.playerOptions, () => {
      Vue.prototype.$player = this.player; // make global

      logService.debug('[PLAYER] VideoJS initialized successfully in Light DOM');

      // Set source immediately - videojs-ima will handle ad playback if configured
      this.setSource();

      // Initialize IMA plugin if ads are enabled
      if (this.playerConfig.advertisement?.enabled) {
        this.initializeIMAPlugin();
      } else {
        // When ads are disabled, emit playerReady event so App can trigger play
        // (When ads are enabled, play is triggered by adsReady event after IMA initializes)
        logService.debug('[PLAYER] Ads disabled - emitting playerReady event');
        this.$emit('playerReady');
      }

      // Call playerReady after everything is set up
      this.playerReady();
    });

    this.bindPlayerEvents();

    const qualitySelector = this.player.getChild('controlBar')?.getChild('QualitySelector');
    qualitySelector?.setIcon('hd');

    const privacyIcon = this.player.getChild('controlBar')?.getChild('PrivacyIcon');
    privacyIcon?.setIcon('circle');
  }

  /**
   * Initialize IMA plugin after VideoJS player is ready
   */
  private initializeIMAPlugin() {
    if (!this.playerConfig.advertisement?.enabled) {
      logService.debug('[PLAYER] Ads disabled, skipping IMA initialization');
      return;
    }

    logService.debug('[PLAYER] Initializing VideoJS-IMA plugin');

    try {
      // Wait for IMA SDK to be available (should be loaded by App.vue)
      if (!window.google?.ima) {
        logService.error('[PLAYER] IMA SDK not loaded');
        return;
      }

      // Initialize IMA plugin with standard configuration
      // Strategy for clip/playlist mode:
      // - Let videojs-ima handle preroll/postroll naturally per clip
      // - Use midroll timer to trigger additional "preroll" requests at intervals
      // - Each "preroll" uses a midroll ad URL, simulating midrolls across the playlist
      this.player.ima({
        adTagUrl: adService.getPrerollUrl(this.playerConfig),
        disableFlashAds: true,
        debug: process.env.NODE_ENV === 'development'
      });

      logService.debug('[PLAYER] VideoJS-IMA plugin initialized');

      // Bind IMA events after plugin initialization
      this.bindImaEvents();

      // NOTE: We do NOT call initializeAdDisplayContainer() here!
      // It will be called in the 'adsready' event handler (bindImaEvents)
      // This ensures proper timing: adsready fires → then initializeAdDisplayContainer() → then play works
      logService.debug('[PLAYER] Waiting for adsready event before initializing ad display container');

    } catch (error) {
      logService.error('[PLAYER] Failed to initialize IMA plugin:', error);
    }
  }

  /**
   * Validate if a playlist item has valid sources and poster
   */
  private isPlaylistItemValid(item: any): boolean {
    if (!item) {
      logService.warn('[PLAYER] Playlist item is null or undefined');
      return false;
    }

    // Check if sources exist and have at least one valid source
    if (!item.sources || !Array.isArray(item.sources) || item.sources.length === 0) {
      logService.warn('[PLAYER] Playlist item has no valid sources:', item);
      return false;
    }

    // Check if at least one source has a valid src URL
    const hasValidSource = item.sources.some((source: any) => source && source.src && source.src.trim() !== '');
    if (!hasValidSource) {
      logService.warn('[PLAYER] Playlist item has no valid source URLs:', item);
      return false;
    }

    // Check if poster (preview image) exists
    if (!item.poster || item.poster.trim() === '') {
      logService.warn('[PLAYER] Playlist item has no preview image (poster):', item);
      return false;
    }

    return true;
  }

  /**
   * Find the next valid playlist item starting from the given index
   * @param startIndex - Index to start searching from
   * @param direction - Search direction: 1 for forward, -1 for backward
   * @returns Index of next valid item, or -1 if none found
   */
  private findNextValidPlaylistItem(startIndex: number, direction: 1 | -1 = 1): number {
    const playlistLength = this.playlist.length;
    let currentIndex = startIndex;

    for (let i = 0; i < playlistLength; i++) {
      currentIndex += direction;

      // Wrap around or stop at boundaries
      if (currentIndex >= playlistLength || currentIndex < 0) {
        return -1; // No valid item found
      }

      const item = this.playlist[currentIndex];
      if (this.isPlaylistItemValid(item)) {
        logService.debug('[PLAYER] Found valid playlist item at index:', currentIndex);
        return currentIndex;
      } else {
        logService.debug('[PLAYER] Skipping invalid playlist item at index:', currentIndex);
      }
    }

    return -1; // No valid item found
  }

  // Playlist control methods
  public playPlaylistItem(index: number) {
    if (this.playlist && this.playlist[index]) {
      // Validate the item before playing
      if (!this.isPlaylistItemValid(this.playlist[index])) {
        logService.warn('[PLAYER] Cannot play invalid playlist item at index:', index);

        // Try to find next valid item
        const nextValidIndex = this.findNextValidPlaylistItem(index, 1);
        if (nextValidIndex !== -1) {
          logService.info('[PLAYER] Auto-skipping to next valid playlist item:', nextValidIndex);
          this.playPlaylistItem(nextValidIndex);
          return;
        } else {
          logService.error('[PLAYER] No valid playlist items found after index:', index);
          this.$emit('templateChange', Template.PLAYER_ERROR);
          return;
        }
      }

      logService.debug('[PLAYER] Playing playlist item:', index);
      this.currentPlaylistIndex = index;

      // Use VideoJS playlist API to switch to the item
      if (this.player && this.player.playlist) {
        this.player.playlist.currentItem(index);
        this.player.play();
      }
    }
  }

  public playPrevious() {
    logService.debug('[PLAYER] Play previous - current index:', this.currentPlaylistIndex);
    if (this.currentPlaylistIndex > 0) {
      // Find the previous valid item
      const prevValidIndex = this.findNextValidPlaylistItem(this.currentPlaylistIndex, -1);
      if (prevValidIndex !== -1) {
        this.playPlaylistItem(prevValidIndex);
      } else {
        logService.warn('[PLAYER] No valid previous playlist item found');
      }
    }
  }

  public playNext() {
    logService.debug('[PLAYER] Play next - current index:', this.currentPlaylistIndex);
    if (this.currentPlaylistIndex < this.playlist.length - 1) {
      // Find the next valid item
      const nextValidIndex = this.findNextValidPlaylistItem(this.currentPlaylistIndex, 1);
      if (nextValidIndex !== -1) {
        this.playPlaylistItem(nextValidIndex);
      } else {
        logService.warn('[PLAYER] No valid next playlist item found');
      }
    }
  }

  /**
   * Public play method - can be called from parent component (App.vue)
   * This triggers the first play which will start the preroll ad
   */
  public play() {
    if (this.player && this.player.play) {
      logService.debug('[PLAYER] play() method called from parent');
      this.player.play();
    } else {
      logService.warn('[PLAYER] play() called but player not ready');
    }
  }

  /**
   * Set live stream or playlist source
   */
  private setSource() {
    if (this.playerConfig.live) {
      this.player.src([
        {
          type: 'application/x-mpegURL',
          src: this.playerConfig.stream!,
        },
      ]);
    } else {
      if (this.playerConfig.playlist && this.playerConfig.playlist.length) {
        // IMPORTANT: Copy playlist to component property for PlayListControls
        this.playlist = this.playerConfig.playlist;

        // Find the first valid playlist item
        let firstValidIndex = 0;
        let foundValid = false;

        for (let i = 0; i < this.playlist.length; i++) {
          if (this.isPlaylistItemValid(this.playlist[i])) {
            firstValidIndex = i;
            foundValid = true;
            logService.info('[PLAYER] Found first valid playlist item at index:', i);
            break;
          } else {
            logService.warn('[PLAYER] Skipping invalid playlist item at index:', i);
          }
        }

        if (!foundValid) {
          logService.error('[PLAYER] No valid playlist items found - all items missing sources or preview images');
          this.$emit('templateChange', Template.PLAYER_ERROR);
          return;
        }

        // If first valid item is not at index 0, we need to reorder or start from that index
        if (firstValidIndex > 0) {
          logService.info('[PLAYER] Starting playlist from first valid item at index:', firstValidIndex);
        }

        // Set playlist for VideoJS player
        this.player.playlist(this.playerConfig.playlist);
        // Play through the playlist automatically.
        this.player.playlist.autoadvance(0);

        // Set the current index to the first valid item
        this.currentPlaylistIndex = firstValidIndex;

        // If the first valid item is not at index 0, jump to it
        if (firstValidIndex > 0) {
          this.player.playlist.currentItem(firstValidIndex);
        }

        this.handleMaxClipDuration();
      } else {
        this.$emit('templateChange', Template.PLAYER_ERROR);
      }
    }
  }

  private bindPlayerEvents() {
    // player events
    this.player.on(PlayerEvent.play, this.playerOnPlay);
    this.player.on(PlayerEvent.playing, this.playerOnPlaying);
    this.player.on(QualityEvent.QUALITY_REQUESTED, this.playerOnQualityChange);
    this.player.on(PlayerEvent.pause, this.playerOnPause);
    this.player.on(PlayerEvent.ended, this.playerOnEnded);
    this.player.on(PlayerEvent.error, this.playerOnError);
    this.player.on(PlayerEvent.timeupdate, this.playerOnTimeupdate);
    this.player.on(PlayerEvent.adserror, this.playerOnAdsError);

    // Sync our currentPlaylistIndex with VideoJS playlist plugin's internal index
    // This is critical for playlist navigation to work correctly
    this.player.on('playlistitem', () => {
      if (this.player && this.player.playlist) {
        const vjsCurrentIndex = this.player.playlist.currentItem();
        if (vjsCurrentIndex !== this.currentPlaylistIndex) {
          logService.debug('[PLAYER] Syncing playlist index from VideoJS:', vjsCurrentIndex);
          this.currentPlaylistIndex = vjsCurrentIndex;
        }
      }
    });

    // Handle VideoJS ad-specific errors that can block video visibility
    // Multiple event names to catch different ad error scenarios
    this.player.on('ads-preroll-error', this.ensureVideoVisibility);
    this.player.on('ads-postroll-error', this.ensureVideoVisibility);
    this.player.on('ads-midroll-error', this.ensureVideoVisibility);
    this.player.on('ads-error', this.ensureVideoVisibility);
    this.player.on('aderror', this.ensureVideoVisibility);

    // DEBUG: Log ALL VideoJS ads events to understand the complete flow
    const debugAdEvents = [
      'ads-preroll-error', 'ads-postroll-error', 'ads-midroll-error', 'ads-error', 'aderror',
      'ads-ready', 'ads-load-start', 'ads-load-end', 'ads-start', 'ads-end',
      'ads-ad-started', 'ads-ad-ended', 'ads-allpods-completed', 'ads-pod-started', 'ads-pod-ended',
      'ads-content-resuming', 'ads-content-resumed', 'contentchanged', 'nopreroll', 'nopostroll'
    ];

    debugAdEvents.forEach(eventName => {
      this.player.on(eventName, (event: any) => {
        logService.trace(`[VIDEOJS ADS] Event: ${eventName}`, event || 'no event data');

        // CRITICAL: In playlist mode, prevent contentchanged from triggering unwanted preroll states
        // contentchanged fires on every clip change, causing black screen delays
        // We only want preroll on initial play and manual midroll triggers
        if (eventName === 'contentchanged' && !this.playerConfig.live && !this.isInitialPlay) {
          logService.debug('[VIDEOJS ADS] contentchanged blocked in playlist mode (prevents clip transition delays)');
          event.stopImmediatePropagation(); // Block the event from triggering ad state changes
          return;
        }

        // CRITICAL: Handle "no ads available" scenarios that block video
        if (eventName === 'nopreroll' || eventName === 'nopostroll') {
          // In playlist mode, postroll events happen at every clip end - we ignore them
          // Only preroll matters (used for both initial preroll and midroll ad breaks)
          if (eventName === 'nopostroll' && !this.playerConfig.live) {
            logService.debug(`[VIDEOJS ADS] ${eventName} - Ignored in playlist mode (expected behavior)`);
            return; // Don't block video visibility for expected postroll events in playlist
          }
          logService.debug(`[VIDEOJS ADS] ${eventName} - No ads available, ensuring video visibility`);
          this.ensureVideoVisibility({ type: eventName });
        }

        // Log CSS state for critical events
        if (eventName.includes('error') || eventName.includes('end') || eventName === 'ads-content-resuming' || eventName.includes('no')) {
          const playerEl = this.player.el() as HTMLElement;
          if (playerEl) {
            logService.trace(`[VIDEOJS ADS] ${eventName} - Player classes:`, playerEl.className);
            const videoEl = playerEl.querySelector('video') as HTMLVideoElement;
            if (videoEl) {
              const computedStyle = window.getComputedStyle(videoEl);
              logService.trace(`[VIDEOJS ADS] ${eventName} - Video opacity:`, computedStyle.opacity);
              logService.trace(`[VIDEOJS ADS] ${eventName} - Video display:`, computedStyle.display);
            }
          }
        }
      });
    });

    this.player.on('useractive', () => (this.isUserActive = true));
    this.player.on('userinactive', () => (this.isUserActive = false));
  }

  private bindImaEvents() {
    // IMA events - only bind if IMA is available and enabled
    if (!this.playerConfig.advertisement?.enabled || !this.player.ima) {
      logService.debug('[PLAYER] IMA not available or disabled, skipping event binding');
      return;
    }

    // CRITICAL: adsready event - this is where we initialize the ad display container
    // This ensures the timing is correct: plugin ready → adsready fires → initialize container → user clicks play
    this.player.on('adsready', () => {
      logService.debug('[PLAYER] adsready event received');

      // Check if Google IMA SDK is properly loaded
      if (typeof window === 'undefined' || !(window as any).google || !(window as any).google.ima) {
        logService.error('[PLAYER] Google IMA SDK not available in adsready event');
        return;
      }

      // CRITICAL: Initialize ad display container NOW (in user interaction context from template switch)
      // This must happen AFTER adsready but BEFORE the first play() call
      if (this.player.ima && this.player.ima.initializeAdDisplayContainer) {
        this.player.ima.initializeAdDisplayContainer();
        logService.debug('[PLAYER] ✅ Ad display container initialized in adsready handler');

        // Emit event to App.vue so it can trigger play() automatically
        this.$emit('adsReady');
        logService.debug('[PLAYER] Emitted adsReady event to parent');
      }

      // @ts-ignore
      this.player.ima.addEventListener(window.google.ima.AdEvent.Type.LOADED, () => {
        logService.trace('>>> ad LOADED');
        this.isAdPending = true;

        // Hide overlays when ad is loaded (content will be hidden by VideoJS)
        this.metaVisible = false;
        this.logoVisible = false;
        logService.debug('[PLAYER] Overlays hidden - ad loaded (sync with content)');

        if (this.midrollTimer) {
          this.midrollTimer.pause();
        }
      });

      // @ts-ignore
      this.player.ima.addEventListener(window.google.ima.AdEvent.Type.STARTED, (adEvent) => {
        this.isLoading = false;
        logService.trace('>>> ad STARTED');

        // Overlays already hidden - no need to hide again
        // (logoVisible/metaVisible are false from init or playMidrollAd())

        const adType = this.getCurrentAdType();
        switch (adType) {
          case 'preroll':
            statisticService.ga('PrerollAdX Start');
            break;
          case 'midroll':
            statisticService.ga('MidrollAdX Start');
            break;
          case 'postroll':
            statisticService.ga('PostrollAdX Start');
            break;
          default:
            statisticService.ga('PrerollAdX Start Ad Start');
        }
      });

      // @ts-ignore
      this.player.ima.addEventListener(window.google.ima.AdEvent.Type.COMPLETE, () => {
        this.isAdComplete = true;
        logService.trace('>>> ad COMPLETE');

        const adType = this.getCurrentAdType();
        switch (adType) {
          case 'preroll':
            statisticService.ga('PrerollAdX Complete');
            break;
          case 'midroll':
            statisticService.ga('MidrollAdX Complete');
            break;
          case 'postroll':
            statisticService.ga('PostrollAdX Complete');
            break;
          default:
            statisticService.ga('Ad endet');
        }
      });

      // @ts-ignore
      this.player.ima.addEventListener(window.google.ima.AdEvent.Type.ALL_ADS_COMPLETED, () => {
        logService.trace('>>> ad ALL_ADS_COMPLETED');

        const adType = this.getCurrentAdType();
        switch (adType) {
          case 'preroll':
            statisticService.ga('PrerollAdX Completed');
            break;
          case 'midroll':
            statisticService.ga('MidrollAdX Completed');
            break;
          case 'postroll':
            statisticService.ga('PostrollAdX Completed');
            break;
          default:
            statisticService.ga('PrerollAdX Start Ad Completed');
        }

        this.isAdPending = false;
        this.currentAdType = null; // Reset ad type

        // CRITICAL CLEANUP: Remove preparing-midroll class and restore volume
        this.player.removeClass('preparing-midroll');
        if (this.savedVolumeBeforeMidroll > 0) {
          this.player.volume(this.savedVolumeBeforeMidroll);
          logService.debug('[PLAYER] Restored volume after ad:', this.savedVolumeBeforeMidroll);
        }

        // Note: Overlays will be shown automatically by playerOnPlaying() event
        logService.debug('[PLAYER] Ad completed - overlays will appear on playing event');

        // Resume content playback and midroll timer
        this.player.play();
        if (this.midrollTimer) {
          this.midrollTimer.resume();
        }
      });

      // CRITICAL: Handle IMA error events for empty VAST responses
      // @ts-ignore
      this.player.ima.addEventListener(window.google.ima.AdErrorEvent.Type.AD_ERROR, (adErrorEvent: any) => {
        logService.warn('[IMA] Ad error event:', adErrorEvent.getError());

        // Force cleanup when ads fail (including empty VAST responses)
        this.ensureVideoVisibility({ type: 'ima-ad-error', error: adErrorEvent.getError() });
      });
    });
  }

  private isPreroll() {
    if (this.player.ima.controller && this.player.ima.controller.settings.adTagUrl.includes('preroll')) {
      return true;
    }
    return false;
  }

  private beforeDestroy() {
    // Clean up Light DOM video element
    this.cleanupLightDOMVideo();

    if (this.player) {
      this.player.dispose();
    }
  }

  /**
   * Clean up Light DOM player container when component is destroyed
   */
  private cleanupLightDOMVideo() {
    // Clean up the entire Light DOM player container
    const playerContainers = document.querySelectorAll('[id^="etv-player-"]');
    playerContainers.forEach(container => {
      container.remove();
      logService.info('[PLAYER] Cleaned up Light DOM player container');
    });

    // Also clean up any standalone video element
    const videoElement = document.getElementById('videoPlayer');
    if (videoElement) {
      videoElement.remove();
      logService.info('[PLAYER] Cleaned up Light DOM video element');
    }
  }

  // Remove the manual startAds method - videojs-ima handles this automatically

  private initializeMidrollAds(intervalSeconds: number): void {
    logService.info(`[PLAYER] Initializing midroll ads with ${intervalSeconds}s interval`);

    // Create midroll timer with precise second counting
    this.midrollTimer = {
      interval: intervalSeconds,
      elapsed: 0,
      paused: false,
      lastTime: 0,
      run: () => {
        if (this.midrollTimer.paused || this.isAdPending) return;

        // Get current playback time
        const currentTimeValue = this.player.currentTime();
        if (typeof currentTimeValue !== 'number' || currentTimeValue <= 0) return;

        const currentTime = Math.floor(currentTimeValue);

        // Only increment if a full second has passed
        // In playlist mode, lastTime resets to 0 for each new clip (set in playerOnPlaying)
        if (currentTime > this.midrollTimer.lastTime) {
          this.midrollTimer.lastTime = currentTime;
          this.midrollTimer.elapsed++;

          logService.trace(`[MIDROLL] Timer: ${this.midrollTimer.elapsed}s / ${this.midrollTimer.interval}s (clip time: ${currentTime}s)`);

          // Time to play midroll ad
          if (this.midrollTimer.elapsed >= this.midrollTimer.interval) {
            logService.info(`[MIDROLL] Interval reached (${this.midrollTimer.elapsed}s) - triggering ad`);
            this.playMidrollAd();
            this.midrollTimer.elapsed = 0; // Reset for next cycle
            this.midrollTimer.lastTime = 0;
          }
        }
      },
      pause: () => {
        logService.debug('[MIDROLL] Timer paused');
        this.midrollTimer.paused = true;
      },
      resume: () => {
        logService.debug('[MIDROLL] Timer resumed');
        this.midrollTimer.paused = false;
      },
      reset: () => {
        logService.debug('[MIDROLL] Timer reset');
        this.midrollTimer.elapsed = 0;
        this.midrollTimer.lastTime = 0;
        this.midrollTimer.paused = false;
      }
    };

    // Start midroll timer on timeupdate
    this.player.on(PlayerEvent.timeupdate, () => {
      if (this.midrollTimer && this.player && typeof this.player.currentTime === 'function') {
        this.midrollTimer.run();
      }
    });

    logService.info('[MIDROLL] Timer initialized and attached to timeupdate event');
  }

  private playMidrollAd(): void {
    logService.info('[PLAYER] Playing midroll ad');
    this.currentAdType = 'midroll';
    this.midrollCounter++; // Increment midroll counter

    const midrollUrl = adService.getMidrollUrl(this.playerConfig, this.midrollCounter);
    logService.info(`[PLAYER] Generated midroll URL #${this.midrollCounter}:`, midrollUrl);

    // Strategy: Trigger a "preroll" with midroll URL
    // In clip/playlist mode, we manually change the ad tag and initiate an ad break
    // This is simpler and more reliable than using requestAds() during content playback

    // Change the ad tag to midroll URL
    if (this.player.ima?.changeAdTag) {
      this.player.ima.changeAdTag(midrollUrl);
      logService.debug('[PLAYER] Changed ad tag to midroll URL');
    } else if (this.player.ima?.controller?.settings) {
      this.player.ima.controller.settings.adTagUrl = midrollUrl;
      logService.debug('[PLAYER] Updated IMA settings with midroll URL');
    }

    // CRITICAL FIX: Prepare for smooth transition to ad
    // 1. Hide play button before pause (prevents flash)
    this.player.addClass('preparing-midroll');

    // 2. Fade out video volume smoothly (300ms)
    const currentVolume = this.player.volume();
    if (typeof currentVolume === 'number' && currentVolume > 0) {
      this.savedVolumeBeforeMidroll = currentVolume; // Save for restoration after ad
      const fadeSteps = 10;
      const fadeInterval = 30; // 300ms total (10 steps * 30ms)
      const volumeStep = currentVolume / fadeSteps;
      let step = 0;

      const volumeFadeInterval = setInterval(() => {
        step++;
        const newVolume = Math.max(0, currentVolume - (volumeStep * step));
        this.player.volume(newVolume);

        if (step >= fadeSteps || newVolume <= 0) {
          clearInterval(volumeFadeInterval);
          this.player.volume(0);
        }
      }, fadeInterval);
    }

    logService.debug('[PLAYER] Started midroll preparation: hiding button, fading volume');
    // Note: Overlays will be hidden by LOADED event (sync with VideoJS content hiding)

    // Pause the player to create an ad break
    this.player.pause();

    // DEBUG: Listen to IMA SDK events for this ad request
    if (this.player.ima) {
      logService.debug('[PLAYER] 🔍 Setting up IMA event listeners for midroll debugging');

      const imaEvents = ['adsready', 'adsManagerLoaded', 'adsManager', 'ads-loader'];
      imaEvents.forEach(evt => {
        this.player.one(evt, (e: any) => {
          logService.debug(`[PLAYER] 🔍 IMA Event: ${evt}`, e);
        });
      });
    }

    // Trigger an ad request (will be treated as preroll by videojs-ima)
    if (this.player.ima?.requestAds) {
      logService.debug('[PLAYER] 🔍 Calling player.ima.requestAds() - watch for IMA errors');
      this.player.ima.requestAds();
      logService.info(`[PLAYER] Midroll ad #${this.midrollCounter} triggered as ad break`);
    } else {
      logService.error('[PLAYER] requestAds() method not available on IMA plugin!');
      // Resume playback if ad request failed
      this.player.play();
    }
  }

  private playPostrollAd(): void {
    logService.info('[PLAYER] Playing postroll ad');
    this.currentAdType = 'postroll';

    // Request postroll ad
    if (this.player.ima?.requestAds) {
      const adRequest = {
        adTagUrl: adService.getPostrollUrl(this.playerConfig),
        linearAdSlotWidth: this.player.width(),
        linearAdSlotHeight: this.player.height(),
      };

      this.player.ima.requestAds(adRequest);
    }
  }

  private getCurrentAdType(): 'preroll' | 'midroll' | 'postroll' | null {
    if (this.currentAdType) {
      return this.currentAdType;
    }

    // Legacy fallback - try to detect from URL
    if (this.player.ima?.controller?.settings?.adTagUrl) {
      const adTagUrl = this.player.ima.controller.settings.adTagUrl;
      if (adTagUrl.includes('preroll')) return 'preroll';
      if (adTagUrl.includes('midroll')) return 'midroll';
      if (adTagUrl.includes('postroll')) return 'postroll';
    }

    return null;
  }

  private playerReady(): void {
    logService.trace('[PLAYER] READY');
    statisticService.ga('Init');

    this.player.tech().on('retryplaylist', async () => {
      this.retryOnError++;

      if (this.retryOnError > 0 && this.retryOnError < 2) {
        logService.warn('retryOnError');
        await etvService.reload();
        this.player.pause();
        this.player.src(this.playerConfig.stream!);
        this.player.load();
        this.player.play();
      }

      if (this.retryOnError >= 2) {
        this.retryOnError = 0;
        logService.error('[PLAYER] retryplaylist error');
        this.$emit('templateChange', Template.PLAYER_ERROR);
      }
    });
  }

  /**
   * player should skip to next clip after defined max duration time
   */
  public handleMaxClipDuration() {
    if (this.playerConfig.clipMaxDuration) {
      /**
       * Set duration to clipMaxDuration if duration is greater than clipMaxDuration
       */
      this.player.on(PlayerEvent.durationchange, () => {
        const duration = this.player.duration();
        if (!duration || !this.playerConfig.clipMaxDuration) return;

        if (duration > this.playerConfig.clipMaxDuration) {
          logService.info(`[PLAYER] Set video duration from ${this.player.duration()}s to ${this.playerConfig.clipMaxDuration}s as defined by config parameter max_duration`);
          this.player.duration(this.playerConfig.clipMaxDuration);
        }
      });

      /**
       * If current time is greater than clipMaxDuration play next video
       */
      this.player.on(PlayerEvent.timeupdate, () => {
        if (!this.playerConfig.clipMaxDuration) return;
        const currentTime = this.player.currentTime() || 0;

        if (currentTime >= this.playerConfig.clipMaxDuration) {
          const nextVideoItem = this.player.playlist.next();
          if (!nextVideoItem) {
            this.$emit('templateChange', Template.STOP);
          }
        }
      });
    }
  }

  public handleClickEvents() {
    if (this.player.paused()) {
      logService.trace('[PLAYER][CLICK] pause');

      if (this.isAdComplete && this.playerConfig) {
        this.pauseTimer = setTimeout(() => {
          logService.info('[PLAYER] handlePauseTimeout ' + Number(process.env.VUE_APP_AUTO_STOP_TIMER_ON_PAUSE) / 1000 + 's');
          this.$emit('templateChange', Template.STOP);
        }, Number(process.env.VUE_APP_AUTO_STOP_TIMER_ON_PAUSE));
      }

      if (this.liveTimeout) {
        logService.info('[PLAYER] reset handleStreamTimeout');
        clearTimeout(this.liveTimeout);
      }
      statisticService.ga('Video Pause');
      statisticService.report('pause', this.playerConfig.stream);
    } else {
      logService.trace('[PLAYER][CLICK] play');
      statisticService.ga('Video Start');
      this.handleStreamTimeout();
    }
  }

  private playerOnQualityChange(event: any, newSource: any) {
    if (!this.playerConfig.live && this.playerConfig.playlist) {
      setSelectedSourcesByQuality(this.player.playlist, this.playerConfig.playlist, newSource);
    }
  }

  private playerOnPlay() {
    logService.info('[PLAYER] onPlay' + (this.isInitialPlay ? ' (initial)' : ''));
    iFrameService.sendStateChangeEvent(this.playerConfig, this.player);

    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
    }

    if (!this.statisticLifecyclePlay) {
      statisticService.ga('Lifecycle Play');
      this.handleStreamTimeout();
      this.statisticLifecyclePlay = true;
    }

    // videojs-ima plugin handles preroll ads automatically on first play
    logService.info('[PLAYER] Play event - videojs-ima plugin will handle ads automatically');
    this.isLoading = false;

    if (this.isInitialPlay) {
      this.isInitialPlay = false;
      statisticService.ga('Video Initial Play');
      statisticService.report('play', this.playerConfig.stream);

      // Initialize midroll ads if enabled in config
      const isMidrollEnabled = this.etvConfig?.template?.advertisement?.midroll?.enabled;
      const midrollInterval = this.etvConfig?.template?.advertisement?.midroll?.interval;

      if (isMidrollEnabled && midrollInterval && midrollInterval > 0) {
        logService.info(`[PLAYER] Midroll enabled with ${midrollInterval}s interval`);
        this.initializeMidrollAds(midrollInterval);
      } else {
        logService.debug('[PLAYER] Midroll ads disabled or interval not set');
      }
    } else {
      statisticService.ga('Video Play');
      statisticService.report('play', this.playerConfig.stream);

      if (this.midrollTimer) {
        this.midrollTimer.run();
      }
    }
  }

  private playerOnPause() {
    logService.info('[PLAYER] onPause');
    iFrameService.sendStateChangeEvent(this.playerConfig, this.player);
    if (this.midrollTimer) {
      this.midrollTimer.pause();
    }
  }


  private playerOnPlaying() {
    logService.info('[PLAYER] onPlaying');

    // If ads are disabled and we're still in LOADING state, transition to PLAYER now
    // (When ads are enabled, this transition happens in adsready event)
    if (!this.playerConfig.advertisement?.enabled) {
      logService.debug('[PLAYER] Content playing without ads - emitting templateChange to PLAYER');
      this.$emit('templateChange', Template.PLAYER);
    }

    // Show both overlays together when content starts playing
    this.handleMetaOverlay(); // This sets metaVisible = true and populates metaInfo
    this.logoVisible = true;
    logService.debug('[PLAYER] Both overlays shown together in onPlaying');

    // Reset prefetch marker when a new clip starts playing (clip/playlist mode only)
    if (!this.playerConfig.live) {
      this.prefetchedNextIndex = null;
    }

    // Reset midroll timer's lastTime when a new clip starts (keeps elapsed cumulative)
    // This ensures the timer continues across clip changes in playlist mode
    if (this.midrollTimer && !this.playerConfig.live) {
      this.midrollTimer.lastTime = 0;
      // Resume timer if it was paused
      if (this.midrollTimer.paused) {
        this.midrollTimer.resume();
      }
      logService.debug(`[MIDROLL] Reset lastTime for new clip, elapsed: ${this.midrollTimer.elapsed}s, timer resumed`);
    }
  }

  private playerOnTimeupdate() {
    const currentTime: any = this.player.currentTime();
    const time = parseInt(currentTime, 10);
    // GA ping alle 30s
    if (time % 30 === 0 && time !== this.timeUpdateSend) {
      this.timeUpdateSend = time;
      statisticService.report('change', this.playerConfig.stream);
      statisticService.ga('Position Change ' + time + 's');
    }

    // Prefetch next clip shortly before the end (clip/playlist mode)
    if (!this.playerConfig.live && this.playerConfig.playlist && this.playerConfig.playlist.length > 0) {
      const duration = this.player.duration() ?? 0;
      // Only proceed with valid, finite duration (VOD/clip)
      if (Number.isFinite(duration) && duration > 0) {
        const secondsLeft = duration - Number(currentTime);
        const PREFETCH_THRESHOLD = 10; // seconds before end
        if (secondsLeft > 0 && secondsLeft <= PREFETCH_THRESHOLD) {
          this.prefetchNextClip();
        }
      }
    }
  }

  // Warm up next clip by hinting the browser to prefetch its media URL
  private prefetchNextClip(): void {
    try {
      if (!this.playerConfig?.playlist || this.playerConfig.playlist.length === 0) return;

      // Determine current playlist index via videojs-playlist if available
      let currentIdx = this.currentPlaylistIndex;
      try {
        // @ts-ignore - videojs-playlist augments the player instance
        if (this.player.playlist && typeof this.player.playlist.currentIndex === 'function') {
          // @ts-ignore
          currentIdx = this.player.playlist.currentIndex();
        }
      } catch (_) {}

      const nextIdx = currentIdx + 1;
      if (this.prefetchedNextIndex === nextIdx) return; // already prefetched
      const nextClip = this.playerConfig.playlist[nextIdx];
      if (!nextClip || !nextClip.sources || nextClip.sources.length === 0) return;

      // Pick the first source; optionally choose by type/quality if needed
      const nextSrc = nextClip.sources[0]?.src;
      if (!nextSrc) return;

      // Avoid duplicate <link> prefetch entries
      if (!document.querySelector(`link[rel=\"prefetch\"][href=\"${nextSrc}\"]`)) {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'fetch';
        link.href = nextSrc;
        document.head.appendChild(link);
        logService.info(`[PLAYER] Prefetching next clip (index ${nextIdx}): ${nextSrc}`);
      }

      this.prefetchedNextIndex = nextIdx;
    } catch (e) {
      logService.warn('[PLAYER] Prefetch next clip failed:', e);
    }
  }

  /**
   * create new pauseable timer to trigger midrolls
   */
  private handleMidroll() {
    // DEPRECATED: Old implementation removed
    // Now using initializeMidrollAds() with timeupdate-based timer
  }

  /**
   * https://github.com/googleads/videojs-ima/blob/main/examples/advanced/ads.js
   * DEPRECATED: Old implementation removed
   */
  private startMidroll() {
    // DEPRECATED: Now using playMidrollAd() with requestAds()
  }

  private playerOnEnded() {
    logService.info('[PLAYER] onEnded');

    this.metaVisible = false;

    // In clip/playlist mode: Do NOT reset isInitialPlay on clip end
    // This ensures midroll timer continues across clips without re-initialization
    // Only reset isInitialPlay if this is truly the last clip or live stream ended
    if (this.playerConfig.live) {
      this.isInitialPlay = true;
    }

    statisticService.ga('Video Completed');
    statisticService.report('finish', this.playerConfig.stream);

    // In clip/playlist mode: Keep midroll timer running across clips
    // Do NOT pause the timer here - it should continue accumulating
    if (this.playerConfig.live && this.midrollTimer) {
      this.midrollTimer.pause();
    }

    // POSTROLL: Disabled for clip/playlist mode
    // Our use case has 15-second clips, so postroll ads don't make sense.
    // Postroll logic has been intentionally removed to avoid interfering with playlist flow.
    // If postroll is needed in the future, ensure it only triggers on the LAST clip, not every clip.
    logService.debug('[PLAYER] Postroll disabled for clip/playlist use case');
  }

  private async playerOnError(event: any) {
    logService.error('[PLAYER] onError', event);

    // In playlist mode, try to skip to the next valid item
    if (!this.playerConfig.live && this.playlist && this.playlist.length > 1) {
      logService.warn('[PLAYER] Video error in playlist mode - attempting to skip to next valid item');

      // Find next valid item
      const nextValidIndex = this.findNextValidPlaylistItem(this.currentPlaylistIndex, 1);
      if (nextValidIndex !== -1) {
        logService.info('[PLAYER] Auto-skipping to next valid playlist item after error:', nextValidIndex);
        // Small delay to allow error state to clear
        setTimeout(() => {
          this.playPlaylistItem(nextValidIndex);
        }, 100);
        return; // Don't show error template yet
      } else {
        logService.error('[PLAYER] No more valid playlist items available after error');
      }
    }

    // If not in playlist mode, or no valid items left, show error
    this.$emit('templateChange', Template.PLAYER_ERROR);
  }

  private async playerOnAdsError(event: any) {
    logService.warn(`[PLAYER] adsError: [${event.data.AdError.h}] ${event.data.AdError.o} - ${event.data.AdError.l}`);
    this.isAdComplete = true;
    this.isLoading = false;
    this.isAdPending = false;

    // CRITICAL: Resume midroll timer after ad error
    if (this.midrollTimer) {
      this.midrollTimer.resume();
      logService.info('[PLAYER] Resumed midroll timer after ad error');
    }

    // CRITICAL: Change template to PLAYER so App.vue removes visibility:hidden
    this.$emit('templateChange', Template.PLAYER);
    logService.info('[PLAYER] Emitted templateChange to PLAYER after ad error');

    // CRITICAL FIX: Add class to force video visibility over vjs-waiting
    const playerEl = this.player.el() as HTMLElement;
    if (playerEl) {
      playerEl.classList.add('ads-content-available');
      logService.info('[PLAYER] Added ads-content-available class in adsError handler');
    }

    // DEBUG: Log current state before any action
    logService.info('[ADS ERROR DEBUG] Player state before cleanup:', {
      isAdComplete: this.isAdComplete,
      isLoading: this.isLoading,
      isAdPending: this.isAdPending
    });

    this.player.play();

    // Additional delayed debugging since VideoJS ads-preroll-error happens AFTER this
    setTimeout(() => {
      logService.info('[PLAYER] Delayed cleanup after IMA ads error');
      this.ensureVideoVisibility({ type: 'delayed-cleanup' });
    }, 200);
  }

  private ensureVideoVisibility(event?: any) {
    logService.debug('[PLAYER] Ensuring video visibility after ad error/no ads:', event?.type || 'unknown event');
    this.isAdComplete = true;
    this.isLoading = false;
    this.isAdPending = false;

    // CRITICAL: Resume midroll timer after ad error/no ads
    if (this.midrollTimer) {
      this.midrollTimer.resume();
      logService.info('[PLAYER] Resumed midroll timer after ad error/no ads');
    }

    // Note: Overlays will be shown automatically by playerOnPlaying() event
    // No need to manually show them here - keeps logic clean and consistent
    logService.debug('[PLAYER] Video visibility ensured - overlays will appear on playing event');

    // CRITICAL FIX: Add class to force video visibility over vjs-waiting
    const playerEl = this.player.el() as HTMLElement;
    if (playerEl) {
      // Remove ALL ad-related classes that might be blocking video
      playerEl.classList.remove('vjs-waiting', 'vjs-ad-loading', 'vjs-ad-playing');
      playerEl.classList.add('ads-content-available');
      logService.debug('[PLAYER] Removed ad blocking classes and added ads-content-available class');

      // AGGRESSIVE: Force remove any IMA overlay containers that might be blocking video
      const imaOverlays = playerEl.querySelectorAll('.ima-ad-container, .ima-controls-div, .ima-countdown-div');
      imaOverlays.forEach(overlay => {
        (overlay as HTMLElement).style.display = 'none';
        logService.trace('[PLAYER] Hidden IMA overlay element:', overlay.className);
      });

      // FORCE: Ensure video element is fully visible
      const videoEl = playerEl.querySelector('video') as HTMLVideoElement;
      if (videoEl) {
        videoEl.style.cssText = `
          opacity: 1 !important;
          visibility: visible !important;
          display: block !important;
          width: 100% !important;
          height: 100% !important;
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          z-index: 1 !important;
          background: #000 !important;
        `;
        logService.debug('[PLAYER] Applied aggressive video visibility styles');
      }      // FORCE: Ensure controls are visible
      const controlBar = playerEl.querySelector('.vjs-control-bar') as HTMLElement;
      if (controlBar) {
        controlBar.style.cssText = `
          display: flex !important;
          visibility: visible !important;
          opacity: 1 !important;
          position: absolute !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 100 !important;
          background: rgba(0,0,0,0.5) !important;
        `;
        logService.debug('[PLAYER] Applied aggressive control bar visibility styles');
      }
    }

    // DEBUGGING: Comprehensive CSS state analysis
    const debugCSSState = () => {
      if (playerEl) {
        logService.trace('[CSS DEBUG] Player container classes:', playerEl.className);
        logService.trace('[CSS DEBUG] Player container style.display:', playerEl.style.display);
        logService.trace('[CSS DEBUG] Player container style.visibility:', playerEl.style.visibility);
        logService.trace('[CSS DEBUG] Player container style.opacity:', playerEl.style.opacity);

        // Check computed styles
        const computedStyle = window.getComputedStyle(playerEl);
        logService.trace('[CSS DEBUG] Player computed display:', computedStyle.display);
        logService.trace('[CSS DEBUG] Player computed visibility:', computedStyle.visibility);
        logService.trace('[CSS DEBUG] Player computed opacity:', computedStyle.opacity);
        logService.trace('[CSS DEBUG] Player computed width:', computedStyle.width);
        logService.trace('[CSS DEBUG] Player computed height:', computedStyle.height);
      }

      // Check video element specifically
      const videoEl = playerEl?.querySelector('video') as HTMLVideoElement;
      if (videoEl) {
        logService.trace('[CSS DEBUG] Video element classes:', videoEl.className);
        logService.trace('[CSS DEBUG] Video element style.display:', videoEl.style.display);
        logService.trace('[CSS DEBUG] Video element style.visibility:', videoEl.style.visibility);
        logService.trace('[CSS DEBUG] Video element style.opacity:', videoEl.style.opacity);

        const videoComputedStyle = window.getComputedStyle(videoEl);
        logService.trace('[CSS DEBUG] Video computed display:', videoComputedStyle.display);
        logService.trace('[CSS DEBUG] Video computed visibility:', videoComputedStyle.visibility);
        logService.trace('[CSS DEBUG] Video computed opacity:', videoComputedStyle.opacity);
        logService.trace('[CSS DEBUG] Video computed width:', videoComputedStyle.width);
        logService.trace('[CSS DEBUG] Video computed height:', videoComputedStyle.height);
        logService.trace('[CSS DEBUG] Video computed position:', videoComputedStyle.position);
        logService.trace('[CSS DEBUG] Video computed z-index:', videoComputedStyle.zIndex);

        // Check actual pixel dimensions
        const rect = videoEl.getBoundingClientRect();
        logService.trace('[CSS DEBUG] Video getBoundingClientRect:', {
          width: rect.width,
          height: rect.height,
          top: rect.top,
          left: rect.left,
          right: rect.right,
          bottom: rect.bottom
        });

        // Check video element properties
        logService.trace('[CSS DEBUG] Video readyState:', videoEl.readyState);
        logService.trace('[CSS DEBUG] Video paused:', videoEl.paused);
        logService.trace('[CSS DEBUG] Video currentTime:', videoEl.currentTime);
        logService.trace('[CSS DEBUG] Video duration:', videoEl.duration);
        logService.trace('[CSS DEBUG] Video src:', videoEl.src);
      } else {
        logService.error('[CSS DEBUG] No video element found in player container!');
      }
    };

    // Debug CSS state immediately
    debugCSSState();

    // IMPORTANT: Do NOT call this.player.play() here as it causes infinite loop!
    // The content should already be playing if it was a play event that triggered this.
    // If we need to ensure playback, we can resume without triggering new play events
    logService.debug('[PLAYER] Video visibility ensured - content should resume naturally');
  }  private handleStreamTimeout() {
    if (this.playerConfig.stopLiveStreamInSec) {
      logService.info('[PLAYER] set handleStreamTimeout ' + Number(this.playerConfig.stopLiveStreamInSec / 60).toFixed(2) + ' min');

      if (this.liveTimeout) {
        clearTimeout(this.liveTimeout);
      }
      this.liveTimeout = setTimeout(() => {
        if (this.playerConfig.stopLiveStreamInSec) {
          logService.info('[PLAYER] handleStreamTimeout ' + Number(this.playerConfig.stopLiveStreamInSec / 60).toFixed(2) + ' min');
        }
        if (this.player.isFullscreen()) {
          this.player.exitFullscreen();
        }

        statisticService.report('stop', this.playerConfig.stream);

        this.$emit('templateChange', Template.STOP);
      }, this.playerConfig.stopLiveStreamInSec * 1000);
    }
  }

  private handleMetaOverlay() {
    // https://earthtv.atlassian.net/browse/WV-126
    if (!this.playerConfig.overlay) return false;

    // set location from playlist clip
    let clipDate = new Date();
    if (!this.playerConfig.live && this.playerConfig.playlist) {
      // @ts-ignore
      const index = this.player.playlist.currentIndex();
      // @ts-ignore
      /* tslint:disable */
      if (this.playerConfig.playlist[index]['location']) {
        this.playerConfig.location = this.playerConfig.playlist[index]['location'];
      }
      if (this.playerConfig.playlist[index]['time']) {
        clipDate = this.playerConfig.playlist[index]['time'];
      }
      /* tslint:enable */
    }

    if (this.playerConfig.location) {
      const lang = this.playerConfig.language;

      // Use language with fallback to English
      this.metaInfo = {
        country: this.playerConfig.location.country[lang] || this.playerConfig.location.country['en'] || '',
        city: this.playerConfig.location.city[lang] || this.playerConfig.location.city['en'] || '',
        date: clipDate,
        timeZone: this.playerConfig.location.timeZone,
      };

      // display live time
      if (this.playerConfig.live) {
        clearInterval(this.clockTimer);
        this.clockTimer = setInterval(() => {
          this.metaInfo.date = new Date();
        }, 5000);
      }

      // Simple rule: Show meta overlay when content is playing
      // It will be automatically hidden by LOADED event when ad starts
      this.metaVisible = true;
      logService.info('[PLAYER] handleMetaOverlay - meta shown with content', this.metaInfo);
    }
  }
}
